import { forwardRef } from "react"

import { useQueryClient } from "@trueskin-web/apis"
import { useAuth } from "@trueskin-web/context"
import { i18nCurrency, i18nLanguage } from "@trueskin-web/locales"
import { useTranslation } from "@trueskin-web/translations"

const envDefaultCurrency = i18nLanguage() === "de" ? "EUR" : i18nCurrency()

const withOrderSummary = (Component) =>
  forwardRef(({ restrictToCurrency = envDefaultCurrency }, ref) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const { user } = useAuth()
    const { data } = queryClient.getQueryData("currentOrderSummary")

    const {
      id: orderId,
      customer: { id: customerId },
      instalments: { count: instalments },
      values: {
        currency,
        total: amountInCents,
        perInstalment: {
          total: perInstalmentPrice,
          currency: perInstalmentCurrency,
        },
      },
      preset: {
        details: { name: productType },
        id: presetId,
      },
      vouchers,
    } = data

    const { voucher: { code: voucherCode = "", discountGroupId = "" } = {} } =
      vouchers.find(
        ({ used, systemDefault, stackable }) =>
          used && !(systemDefault || stackable)
      ) || {}

    const hasTwoMonthPackage = instalments === 2

    const order = {
      customerId,
      orderId,
      productType,
      presetId,
      voucherCode,
      discountGroupId,
      hasTwoMonthPackage,
      amount: hasTwoMonthPackage
        ? perInstalmentPrice / 100
        : amountInCents / 100,
      amountInCents: hasTwoMonthPackage ? perInstalmentPrice : amountInCents,
      currency: hasTwoMonthPackage ? perInstalmentCurrency : currency,
      v2CustomerId: user.v2CustomerId,
      accountId: user.accountId,
    }

    if (restrictToCurrency && currency !== restrictToCurrency) {
      const {
        total: restrictedCurrencyAmountInCents,
        currency: restrictedCurrency,
      } =
        data.values.otherCurrencies.find(
          (value) => value.currency === restrictToCurrency
        ) || {}

      if (!restrictedCurrency) {
        throw new Error(t("Generic.errors.invalidPaymentCurrency"))
      }

      const orderRestrictedCurrency = {
        ...order,
        amount: restrictedCurrencyAmountInCents / 100,
        amountInCents: restrictedCurrencyAmountInCents,
        currency: restrictedCurrency,
      }

      return <Component ref={ref} order={orderRestrictedCurrency} />
    }

    return <Component ref={ref} order={order} />
  })

export default withOrderSummary
